import React, { useEffect, useRef, useState } from 'react';

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number; // in milliseconds
  variant?: 'fade-up' | 'fade-in' | 'image';
  threshold?: number;
}

/**
 * ScrollReveal Component
 * Provides clean, silky-smooth, hardware-accelerated on-load & scroll animations.
 * Uses lightweight IntersectionObserver with CSS GPU compositor transitions for zero jitter/lag.
 */
export const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className = '',
  delay = 0,
  variant = 'fade-up',
  threshold = 0.12
}) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const domRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = domRef.current;
    if (!element) return;

    // Immediate reveal if IntersectionObserver not available or if already in viewport
    if (!('IntersectionObserver' in window)) {
      setIsRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -30px 0px'
      }
    );

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [threshold]);

  const baseClass = variant === 'image' ? 'reveal-img-init' : 'reveal-init';

  return (
    <div
      ref={domRef}
      className={`${baseClass} ${isRevealed ? 'is-revealed' : ''} ${className}`}
      style={{
        transitionDelay: delay ? `${delay}ms` : undefined
      }}
    >
      {children}
    </div>
  );
};
